import React, { useEffect, useState } from 'react';
import { Instagram, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Live grid of the latest 6 posts from @umapremium on Instagram.
 *
 * Renders nothing when the backend reports `configured: false` (no Meta token
 * in env) — keeps the homepage clean until the owner completes the Meta app
 * setup and drops `INSTAGRAM_ACCESS_TOKEN` + `INSTAGRAM_USER_ID` into backend/.env.
 */
const InstagramGrid = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/public/instagram`);
        if (cancelled) return;
        if (!data.configured || !data.items?.length) {
          setVisible(false);
          return;
        }
        setItems(data.items.slice(0, 6));
      } catch {
        if (!cancelled) setVisible(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!visible) return null;

  return (
    <section id="instagram" className="py-20 bg-gradient-to-b from-stone-50 to-white" data-testid="instagram-grid">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 rounded-full text-amber-700 text-sm font-medium mb-4">
            <Instagram className="w-4 h-4" /> @umapremium
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-3">
            {t('instagram.title') || 'Live from the terrace'}
          </h2>
          <p className="text-stone-600 max-w-xl mx-auto">
            {t('instagram.subtitle') || 'Follow our stories, sunsets, and cocktails. New posts appear here automatically.'}
          </p>
        </div>
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-square rounded-2xl bg-stone-200 animate-pulse" data-testid={`ig-skel-${i}`} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {items.map((p) => {
              const img = p.media_url || p.thumbnail_url;
              return (
                <a
                  key={p.id}
                  href={p.permalink}
                  target="_blank"
                  rel="noreferrer"
                  className="group relative aspect-square overflow-hidden rounded-2xl bg-stone-200 shadow-sm hover:shadow-xl transition-shadow"
                  data-testid={`ig-item-${p.id}`}
                  title={p.caption || 'View on Instagram'}
                >
                  {img ? (
                    <img
                      src={img}
                      alt={(p.caption || '@umapremium post').slice(0, 120)}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  ) : (
                    <div className="w-full h-full bg-stone-100 flex items-center justify-center text-stone-400">
                      <Instagram className="w-8 h-8" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                    <p className="text-white text-xs line-clamp-3 leading-snug">{p.caption || 'View on Instagram'}</p>
                  </div>
                  {p.media_type === 'VIDEO' && (
                    <span className="absolute top-2 right-2 bg-black/50 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-widest">Reel</span>
                  )}
                </a>
              );
            })}
          </div>
        )}
        <div className="text-center mt-8">
          <a
            href="https://instagram.com/umapremium"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-stone-900 text-white text-sm font-semibold hover:bg-stone-800 transition-colors"
            data-testid="ig-follow-btn"
          >
            <Instagram className="w-4 h-4" /> Follow @umapremium <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default InstagramGrid;
